'use client';

import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';
import { Tabs as Root, TabsContent, TabsList, TabsTrigger } from 'ui/tabs';

const tabsListVariants = cva('p-0 ', {
  variants: {
    variant: {
      default: '',
      fullWidth: 'w-full border border-primary rounded-lg',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const tabsTriggerVariants = cva('cursor-pointer', {
  variants: {
    variant: {
      default: '',
      fullWidth:
        'w-full data-[state=active]:rounded-lg py-3 flex justify-center items-center cursor-pointer text-primary ',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type TabItem = {
  label: React.ReactNode;
  value: string;
  content: React.ReactNode;
  disabled?: boolean;
};

type TabsProps = {
  defaultActive: string;
  value?: string;
  items: TabItem[];
  className?: string;
  triggerClassName?: string;
  listClassName?: string;
  onChange?: (value: string) => void;
} & VariantProps<typeof tabsListVariants>;

export const Tabs = ({
  defaultActive,
  value,
  items,
  className,
  triggerClassName,
  listClassName,
  variant = 'default',
  onChange,
}: TabsProps) => {
  const [activeTab, setActiveTab] = React.useState(defaultActive);
  const selectedTab = value ?? activeTab;
  const handleChange = (nextValue: string) => {
    if (value === undefined) setActiveTab(nextValue);
    onChange?.(nextValue);
  };

  return (
    <Root defaultValue={defaultActive} value={selectedTab} onValueChange={handleChange} className={className}>
      <TabsList className={cn(tabsListVariants({ variant }), listClassName)}>
        {items.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            className={cn(tabsTriggerVariants({ variant }), triggerClassName)}
            disabled={tab.disabled}
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {items.map((tab) => (
        <TabsContent key={tab.value} value={tab.value} className="mt-0">
          {tab.content}
        </TabsContent>
      ))}
    </Root>
  );
};
